<template>
  <div class="wellness-module">
    <!-- Latest Mood Section -->
    <div class="wellness-section">
      <div class="section-header">
        <h3>Mood and Wellness</h3>
        <span v-if="Object.keys(latestMood).length !== 0" class="section-date">
          Last submitted: {{ formatDateForHuman(latestMood.date) }}
        </span>
      </div>

      <div v-if="Object.keys(latestMood).length !== 0" class="wellness-grid">
        <div v-for="metric in metricsConfig" :key="metric.key"
          class="wellness-card" :style="{ borderTopColor: getScoreColor(+latestMood[metric.key]) }">
          <div class="card-label">{{ metric.title }}</div>
          <img :src="getScoreImage(+latestMood[metric.key])" :alt="getScoreLabel(metric.key, +latestMood[metric.key])" />
          <div class="card-score-badge" :style="{ backgroundColor: getScoreColor(+latestMood[metric.key]) }">
            {{ +latestMood[metric.key] }}/5
          </div>
          <div class="card-status">{{ getScoreLabel(metric.key, +latestMood[metric.key]) }}</div>
        </div>
      </div>
      <div v-else class="empty-state">
        <img src="./img/emptyStateIllustration.svg" />
        <p>No mood and wellness data</p>
      </div>
    </div>

    <!-- Season Averages Section -->
    <div v-if="seasionsPlayerMoodDataAvg.sleep !== null" class="wellness-section">
      <div class="section-header">
        <h3>Season Averages</h3>
      </div>

      <div class="wellness-grid">
        <div v-for="metric in metricsConfig" :key="'avg-' + metric.key"
          class="wellness-card clickable" :style="{ borderTopColor: getScoreColor(+seasionsPlayerMoodDataAvg[metric.key]) }"
          @click="selectMetricTab(metric.key)">
          <div class="card-label">{{ metric.title }}</div>
          <img :src="getScoreImage(+seasionsPlayerMoodDataAvg[metric.key])" :alt="getScoreLabel(metric.key, +seasionsPlayerMoodDataAvg[metric.key])" />
          <div class="card-score-badge" :style="{ backgroundColor: getScoreColor(+seasionsPlayerMoodDataAvg[metric.key]) }">
            {{ +seasionsPlayerMoodDataAvg[metric.key] }}/5
          </div>
          <div class="card-status">{{ getScoreLabel(metric.key, +seasionsPlayerMoodDataAvg[metric.key]) }}</div>
          <div class="card-chart-hint"><i class="bi bi-graph-up"></i></div>
        </div>
      </div>
    </div>

    <!-- Trend Charts Section -->
    <div v-if="thisSeasionsPlayerMoodData && thisSeasionsPlayerMoodData.length > 0" class="wellness-section charts-section">
      <div class="section-header">
        <h3>Trends Over Time</h3>
      </div>

      <div class="chart-tabs">
        <button v-for="metric in metricsConfig" :key="'tab-' + metric.key"
          :class="['chart-tab', { active: selectedMetric === metric.key }]"
          @click="selectMetricTab(metric.key)">
          {{ metric.title }}
        </button>
      </div>

      <div class="time-range-tabs">
        <button v-for="range in timeRanges" :key="range.key"
          :class="['time-range-tab', { active: selectedTimeRange === range.key }]"
          @click="selectTimeRange(range.key)">
          {{ range.label }}
        </button>
      </div>

      <div class="chart-container">
        <WellnessLineChart :chartData="lineChartData" :metricType="selectedMetric" :key="selectedMetric" />
      </div>
    </div>

    <!-- Full-screen chart modal -->
    <b-modal
      v-if="showPlayerMoodBarChartModal"
      v-model="showPlayerMoodBarChartModal"
      size="xl"
      modal-class="modal-fullscreen"
      :title="'Wellness Trend — ' + getMetricTitle(selectedMetric)"
      :hide-footer="Boolean(true)"
    >
      <div class="modal-chart-container">
        <WellnessLineChart :chartData="lineChartData" :metricType="selectedMetric" />
      </div>
    </b-modal>
  </div>
</template>

<script>
import moment from "moment";
import WellnessLineChart from "../../components/WellnessLineChart";
import { normaliseEntry, DISPLAY_SCALE } from "@/utils/wellnessScale";
export default {
  name: "wellnessMoodSummary",
  components: { WellnessLineChart },
  data() {
    return {
      showPlayerMoodBarChartModal: false,
      selectedMetric: "sleep",
      selectedTab: "sleep-tab",
      seasionsPlayerMoodDataAvg: {
        fatigue: null,
        happyness: null,
        muscle_soreness: null,
        sleep: null,
        stress: null,
      },
      playerMoodBarChartDataSet: {},
      lineChartData: { labels: [], datasets: [] },
      selectedTimeRange: "season",
      timeRanges: [
        { key: "7d", label: "7 Days" },
        { key: "30d", label: "30 Days" },
        { key: "90d", label: "90 Days" },
        { key: "season", label: "Season" },
      ],
      metricsConfig: [
        { key: "sleep", title: "Sleep" },
        { key: "happyness", title: "Happiness" },
        { key: "muscle_soreness", title: "Muscle Soreness" },
        { key: "fatigue", title: "Fatigue" },
        { key: "stress", title: "Stress" },
      ],
    };
  },
  props: {
    playerMoodData: {
      type: Object,
      default: null,
    },
    thisSeasionsPlayerMoodData: {
      type: Array,
      default: null,
    },
  },
  computed: {
    // Latest entry on the 1–5 scale this component's labels, colours and
    // images are built on (older web entries arrive on 1–7 and are converted).
    latestMood() {
      const raw = this.playerMoodData;
      if (!raw || Object.keys(raw).length === 0) return {};
      const n = normaliseEntry(raw, DISPLAY_SCALE);
      ["sleep", "happyness", "stress", "fatigue", "muscle_soreness"].forEach((k) => {
        if (n[k] !== undefined && n[k] !== null) n[k] = Math.round(Number(n[k]));
      });
      return n;
    },
    // Season entries on the same 1–5 scale.
    seasonMoodNormalised() {
      const arr = Array.isArray(this.thisSeasionsPlayerMoodData) ? this.thisSeasionsPlayerMoodData : [];
      return arr.map((e) => normaliseEntry(e, DISPLAY_SCALE));
    },
  },
  async mounted() {
    this.seasionsPlayerMoodDataAvg = this.getPlayerWellnessAvData();
    this.buildLineChartData("sleep");
  },
  watch: {
    thisSeasionsPlayerMoodData() {
      this.seasionsPlayerMoodDataAvg = this.getPlayerWellnessAvData();
      this.buildLineChartData(this.selectedMetric);
    },
  },
  methods: {
    selectMetricTab(metricKey) {
      this.selectedMetric = metricKey;
      this.buildLineChartData(metricKey);
    },
    selectTimeRange(rangeKey) {
      this.selectedTimeRange = rangeKey;
      this.buildLineChartData(this.selectedMetric);
    },
    filterDataByTimeRange(data) {
      if (!data || data.length === 0) return data;
      if (this.selectedTimeRange === "season") return data;

      const now = moment();
      const daysMap = { "7d": 7, "30d": 30, "90d": 90 };
      const days = daysMap[this.selectedTimeRange];
      if (!days) return data;

      const cutoff = now.subtract(days, "days").startOf("day");
      return data.filter((entry) => moment(entry.date).isSameOrAfter(cutoff));
    },
    getMetricTitle(key) {
      const metric = this.metricsConfig.find((m) => m.key === key);
      return metric ? metric.title : key;
    },
    showGraph(type) {
      this.selectedMetric = type;
      this.buildLineChartData(type);
      this.showPlayerMoodBarChartModal = true;
    },
    formatDateForHuman(date) {
      return date !== undefined && moment(date).isValid()
        ? moment(date).format("MMMM Do YYYY")
        : "";
    },
    getScoreColor(score) {
      const colorMap = {
        5: "#4caf50",
        4: "#66bb6a",
        3: "#ffc107",
        2: "#ff9800",
        1: "#b71c1c",
      };
      return colorMap[score] || "#9e9e9e";
    },
    getScoreImage(score) {
      if (score >= 5) return require("./img/amazing.svg");
      if (score === 4) return require("./img/good.svg");
      if (score === 3) return require("./img/ok.svg");
      if (score === 2) return require("./img/notSoGood.svg");
      return require("./img/bad.svg");
    },
    getScoreLabel(metricKey, score) {
      const labels = {
        sleep: { 5: "Amazing", 4: "Good", 3: "Ok", 2: "Not so good", 1: "Bad" },
        happyness: { 5: "Very happy", 4: "Happy", 3: "Feeling ok", 2: "Not so good", 1: "Unhappy" },
        muscle_soreness: { 5: "None", 4: "Feeling ok", 3: "Some soreness", 2: "Sore muscles", 1: "Very sore" },
        fatigue: { 5: "None", 4: "Bit tired", 3: "Sluggish", 2: "Very tired", 1: "Exhausted" },
        stress: { 5: "Feeling great", 4: "Feeling good", 3: "Ok", 2: "Bit stressed", 1: "Very stressed" },
      };
      return labels[metricKey]?.[score] || "Unknown";
    },
    average(array) {
      if (array === undefined || array === null || array.length === 0) {
        return null;
      }
      return array.reduce((a, b) => Number(a) + Number(b), 0) / array.length;
    },
    getPlayerWellnessAvData() {
      if (!this.thisSeasionsPlayerMoodData || this.thisSeasionsPlayerMoodData.length === 0) {
        return { sleep: null, happyness: null, muscle_soreness: null, fatigue: null, stress: null };
      }

      const metrics = ["sleep", "happyness", "muscle_soreness", "fatigue", "stress"];
      const moodDataAv = {};

      metrics.forEach((metric) => {
        const values = this.seasonMoodNormalised.map((entry) => entry[metric]);
        const avg = this.average(values);
        moodDataAv[metric] = avg !== null ? Math.round(avg) : null;
      });

      this.moodDataAv = moodDataAv;
      return moodDataAv;
    },
    buildLineChartData(type) {
      const emptyChart = { labels: [], datasets: [] };

      if (!this.thisSeasionsPlayerMoodData || this.thisSeasionsPlayerMoodData.length === 0) {
        this.lineChartData = emptyChart;
        return;
      }

      const filtered = this.filterDataByTimeRange(this.seasonMoodNormalised);
      if (!filtered || filtered.length === 0) {
        this.lineChartData = emptyChart;
        return;
      }

      const labels = filtered.map((entry) => entry.date);
      const data = filtered.map((entry) => Number(entry[type]));

      const colorMap = {
        sleep: "54, 162, 235",
        happyness: "255, 193, 7",
        muscle_soreness: "233, 30, 99",
        fatigue: "156, 39, 176",
        stress: "76, 175, 80",
      };
      const rgb = colorMap[type] || "54, 162, 235";

      this.lineChartData = {
        labels: labels,
        datasets: [
          {
            label: type,
            data: data,
            borderColor: `rgba(${rgb}, 1)`,
            backgroundColor: `rgba(${rgb}, 0.15)`,
            pointBackgroundColor: "#fff",
            pointBorderColor: `rgba(${rgb}, 1)`,
            pointBorderWidth: 2,
            fill: true,
          },
        ],
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.wellness-module {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.wellness-section {
  .section-header {
    display: flex;
    align-items: baseline;
    gap: 1rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;

    h3 {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 600;
      text-transform: none;
    }

    .section-date {
      font-size: 0.85rem;
      color: #6c757d;
    }
  }
}

.wellness-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;

  @media (min-width: 768px) {
    grid-template-columns: repeat(5, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
  }
}

.wellness-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-top: 4px solid #9e9e9e;
  border-radius: 8px;
  padding: 1rem 0.75rem;
  text-align: center;
  transition: box-shadow 0.2s, transform 0.2s;
  position: relative;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &.clickable {
    cursor: pointer;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.12);
    }
  }

  .card-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: #495057;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
  }

  img {
    display: block;
    margin: 0 auto;
    height: 3.5rem;
    width: auto;
  }

  .card-score-badge {
    display: inline-block;
    margin-top: 0.5rem;
    padding: 0.15rem 0.6rem;
    border-radius: 12px;
    color: #fff;
    font-size: 0.75rem;
    font-weight: 700;
  }

  .card-status {
    margin-top: 0.35rem;
    font-size: 0.85rem;
    color: #343a40;
    font-weight: 500;
  }

  .card-chart-hint {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    color: #adb5bd;
    font-size: 0.75rem;
  }
}

// Chart Tabs
.chart-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.chart-tab {
  padding: 0.4rem 1rem;
  border: 1px solid #dee2e6;
  border-radius: 20px;
  background: #fff;
  color: #495057;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }

  &.active {
    background: #0d6efd;
    color: #fff;
    border-color: #0d6efd;
  }
}

.time-range-tabs {
  display: flex;
  gap: 0.35rem;
  margin-bottom: 1rem;
}

.time-range-tab {
  padding: 0.3rem 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  background: #fff;
  color: #6c757d;
  font-size: 0.78rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }

  &.active {
    background: #495057;
    color: #fff;
    border-color: #495057;
  }
}

.chart-container {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
}

.modal-chart-container {
  padding: 1rem;
  min-height: 400px;
}

.empty-state {
  text-align: center;
  padding: 2rem;
  color: #6c757d;

  img {
    max-width: 120px;
    opacity: 0.6;
    margin-bottom: 1rem;
  }

  p {
    font-size: 0.9rem;
  }
}
</style>
